import { useEffect } from "react";

const BASE_TITLE = "Competition Checker";
const SITE_URL = "https://competitionchecker.com";
const DEFAULT_OG_IMAGE = "/og-image.png";

// All meta/link/script tags we manage are tagged with `data-seo="cc"` so we can
// remove them cleanly when the page unmounts and avoid orphan tags polluting
// the next route's <head>.
function upsertMeta(attr, key, content) {
  if (!content && content !== 0) return null;
  let el = document.head.querySelector(`meta[${attr}="${key}"][data-seo="cc"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    el.setAttribute("data-seo", "cc");
    document.head.appendChild(el);
  }
  el.setAttribute("content", String(content));
  return el;
}

function upsertLink(rel, href) {
  if (!href) return null;
  let el = document.head.querySelector(`link[rel="${rel}"][data-seo="cc"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    el.setAttribute("data-seo", "cc");
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
  return el;
}

function clearJsonLd(testId) {
  document.head
    .querySelectorAll(`script[type="application/ld+json"][data-seo="cc"][data-seo-id="${testId}"]`)
    .forEach((s) => s.remove());
}

function clearManagedJsonLd() {
  document.head
    .querySelectorAll(
      'script[type="application/ld+json"][data-seo="cc"]',
    )
    .forEach((script) => script.remove());
}

function injectJsonLd(payload, testId = "default") {
  if (!payload) return;
  clearJsonLd(testId);
  const script = document.createElement("script");
  script.type = "application/ld+json";
  script.setAttribute("data-seo", "cc");
  script.setAttribute("data-seo-id", testId);
  script.setAttribute("data-testid", `jsonld-${testId}`);
  script.text = JSON.stringify(payload);
  document.head.appendChild(script);
}

function defaultJsonLd({ canonical, description, path, title }) {
  const collectionPaths = new Set([
    "/competitions",
    "/uk-competitions",
    "/uk-car-competitions",
    "/operators",
    "/best-value",
    "/best-odds-for-budget",
    "/guides",
  ]);
  const breadcrumbId = `${canonical}#breadcrumb`;
  return {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": collectionPaths.has(path) ? "CollectionPage" : "WebPage",
        "@id": `${canonical}#webpage`,
        url: canonical,
        name: title,
        description,
        isPartOf: { "@id": `${SITE_URL}/#website` },
        breadcrumb: { "@id": breadcrumbId },
      },
      {
        "@type": "BreadcrumbList",
        "@id": breadcrumbId,
        itemListElement: [
          {
            "@type": "ListItem",
            position: 1,
            name: BASE_TITLE,
            item: `${SITE_URL}/`,
          },
          {
            "@type": "ListItem",
            position: 2,
            name: title,
            item: canonical,
          },
        ],
      },
    ],
  };
}

/**
 * <SEO />
 *
 * Manages <title>, description, canonical, OpenGraph + Twitter card meta tags,
 * and any JSON-LD blocks for the current route.
 *
 * Image safety rule (iter 6/7): the caller is responsible for only passing
 * `image` when the asset_resolution_status allows it. If `image` is falsy we
 * fall back to the brand default OG image. We never accept pending/mismatch
 * image URLs as og:image.
 */
export default function SEO({
  title,
  exactTitle = false,
  description,
  path,
  image,
  imageAlt,
  ogType = "website",
  noIndex = false,
  queryString,
  jsonLd,        // single object or array of objects
  jsonLdId = "default",
}) {
  useEffect(() => {
    const fullTitle = title
      ? exactTitle || title.includes(BASE_TITLE)
        ? title
        : `${title} | ${BASE_TITLE}`
      : `${BASE_TITLE} — Check the real odds before you enter.`;
    document.title = fullTitle;

    upsertMeta("name", "description", description || "");
    const activeQuery =
      queryString !== undefined
        ? String(queryString)
        : typeof window !== "undefined"
          ? window.location.search
          : "";
    const queryVariant = Boolean(activeQuery);
    upsertMeta(
      "name",
      "robots",
      noIndex
        ? "noindex, nofollow"
        : queryVariant
          ? "noindex, follow"
          : "index, follow",
    );
    const requestedPath =
      path ||
      (typeof window !== "undefined" ? window.location.pathname : "/");
    const canonicalPath = new URL(requestedPath, SITE_URL).pathname;
    const url = SITE_URL + canonicalPath;

    // OpenGraph
    upsertMeta("property", "og:site_name", BASE_TITLE);
    upsertMeta("property", "og:type", ogType);
    upsertMeta("property", "og:title", fullTitle);
    upsertMeta("property", "og:description", description || "");
    upsertMeta("property", "og:url", url);
    const safeImage = image || DEFAULT_OG_IMAGE;
    const absImage = safeImage.startsWith("http")
      ? safeImage
      : SITE_URL + (safeImage.startsWith("/") ? safeImage : `/${safeImage}`);
    upsertMeta("property", "og:image", absImage);
    upsertMeta("property", "og:image:alt", imageAlt || title || BASE_TITLE);

    // Twitter / X
    upsertMeta("name", "twitter:card", "summary_large_image");
    upsertMeta("name", "twitter:title", fullTitle);
    upsertMeta("name", "twitter:description", description || "");
    upsertMeta("name", "twitter:image", absImage);
    upsertMeta("name", "twitter:image:alt", imageAlt || title || BASE_TITLE);

    // Canonical
    upsertLink("canonical", url);

    // JSON-LD
    clearManagedJsonLd();
    const effectiveJsonLd =
      jsonLd ||
      (canonicalPath === "/"
        ? null
        : defaultJsonLd({
            canonical: url,
            description: description || "",
            path: canonicalPath,
            title,
          }));
    if (effectiveJsonLd && !queryVariant && !noIndex) {
      injectJsonLd(effectiveJsonLd, jsonLdId);
    } else {
      clearJsonLd(jsonLdId);
    }

    return () => {
      // Strip JSON-LD on unmount so a stale block doesn't leak into the next
      // route (other meta tags are upserted, so they'll be overwritten).
      clearJsonLd(jsonLdId);
    };
  }, [title, exactTitle, description, path, image, imageAlt, ogType, noIndex, queryString, jsonLd, jsonLdId]);

  return null;
}
